import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Mail } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { api } from '@/lib/api';
import { useCanEdit } from '@/lib/queries';
import { date, dateTime, gradeLabel, money } from '@/lib/format';
import { adminLinks, gocardless, paypal } from '@/lib/links';
import { ErrorNote, Spinner } from '@/components/Layout';
import { History } from '@/components/admin/History';
import { EMAIL_COLUMNS, EmailsSent, type EmailRow } from '@/components/admin/EmailsSent';
import { PaymentActionForm, PaymentActions, ROW_ACTION, type PaymentAction } from '@/components/admin/Refund';
import { PAYMENT_PILL } from '@/components/admin/Settle';
import { ledgerHow, type LedgerRow } from './AdminPayments';

type Named = { id: string; first_name: string; last_name: string } | null;
const nameOf = (p: Named) => p ? `${p.first_name} ${p.last_name}` : '';
const personLink = (p: Named) => p && <Link to={adminLinks.person(p.id)} className="link">{nameOf(p)}</Link>;
const statusText = (s: string) => s === 'pending' ? 'Pending' : s.replace('_', ' ');
const Pill = ({ s }: { s: string }) => <span className={`pill capitalize ${PAYMENT_PILL[s] ?? 'bg-stone-100 text-ink-700'}`}>{statusText(s)}</span>;

/** A label and value; nothing at all when there is no value. */
function Row({ label, children }: { label: string; children: ReactNode }) {
  if (children === null || children === undefined || children === '' || children === false) return null;
  return <div className="flex gap-4 border-b border-stone-100 py-2 last:border-0"><dt className="w-44 shrink-0 text-ink-500">{label}</dt><dd className="min-w-0">{children}</dd></div>;
}

/** Who did something, from their login: a linked name, or "an admin" for a login with no member record. */
function useNamesByLogin(ids: (string | null | undefined)[]) {
  const wanted = [...new Set(ids.filter((x): x is string => !!x))].sort();
  return useQuery({
    queryKey: ['names-by-login', wanted.join()],
    enabled: wanted.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.from('person').select('id,first_name,last_name,user_id').in('user_id', wanted);
      if (error) throw error;
      return new Map((data as (NonNullable<Named> & { user_id: string })[]).map(p => [p.user_id, p]));
    },
  });
}

/**
 * One line of the ledger: a payment (with what it paid for, payments towards it,
 * refunds and receipts) or a refund or chargeback (with the payment it came from).
 */
export function AdminPayment() {
  const { id = '' } = useParams();
  const line = useQuery({
    queryKey: ['admin-transaction', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('ledger').select('*').eq('id', id).maybeSingle();
      if (error) throw error;
      return data as LedgerRow | null;
    },
  });
  if (line.isLoading) return <Spinner />;
  if (line.error) return <ErrorNote>Could not load this payment.</ErrorNote>;
  if (!line.data) return <ErrorNote>There is no payment or refund with that link.</ErrorNote>;
  return line.data.kind === 'payment' ? <PaymentView line={line.data} /> : <RefundView line={line.data} />;
}

interface PaymentRecord {
  id: string; household_id: string; provider: string; provider_payment_id: string | null; checkout_ref: string | null; amount_pence: number;
  discount_pence: number; discount_reason: string | null; status: string; description: string | null; charge_date: string | null; created_at: string;
  reference: string | null; note: string | null; recorded_by: string | null; parent_payment_id: string | null; payment_method_id: string | null;
  household: { id: string; name: string } | null; collected_by: Named;
  memberships: { id: string; starts_on: string; ends_on: string; price_pence: number; status: string; person: Named; plan: { name: string } | null }[];
  bookings: { id: string; price_pence: number; status: string; person: Named; option: { name: string } | null; event: { id: string; title: string } | null }[];
  fees: { id: string; fee_pence: number | null; for_rank: number; person: Named; event: { id: string; title: string } | null }[];
}

function PaymentView({ line }: { line: LedgerRow }) {
  const id = line.id;
  const qc = useQueryClient();
  const canEdit = useCanEdit();
  const [acting, setActing] = useState<PaymentAction | null>(null);
  const [receipt, setReceipt] = useState('');
  const pay = useQuery({
    queryKey: ['admin-payment', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('payment')
        .select('id,household_id,provider,provider_payment_id,checkout_ref,amount_pence,discount_pence,discount_reason,status,description,charge_date,created_at,reference,note,recorded_by,parent_payment_id,payment_method_id,household:household_id(id,name),collected_by:collected_by_person_id(id,first_name,last_name),memberships:membership(id,starts_on,ends_on,price_pence,status,person:person_id(id,first_name,last_name),plan:plan_id(name)),bookings:event_registration(id,price_pence,status,person:person_id(id,first_name,last_name),option:option_id(name),event:event_id(id,title)),fees:grading!grading_fee_payment_id_fkey(id,fee_pence,for_rank,person:person_id(id,first_name,last_name),event:event_id(id,title))')
        .eq('id', id).single();
      if (error) throw error;
      return data as unknown as PaymentRecord;
    },
  });
  // Related transactions: balance payments towards this one, and refunds of it or of them.
  const after = useQuery({
    queryKey: ['admin-payment-after', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('ledger').select('*').or(`parent_payment_id.eq.${id},payment_id.eq.${id}`).neq('id', id).order('created_at');
      if (error) throw error;
      return data as LedgerRow[];
    },
  });
  const emails = useQuery({
    queryKey: ['admin-payment-emails', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('email_log').select(EMAIL_COLUMNS).or(`dedupe_key.like.receipt:${id}*,dedupe_key.eq.payment_failed:${id}`).order('created_at', { ascending: false });
      if (error) throw error;
      return data as EmailRow[];
    },
  });
  const names = useNamesByLogin([pay.data?.recorded_by]);
  if (pay.isLoading) return <Spinner />;
  if (pay.error || !pay.data) return <ErrorNote>Could not load this payment.</ErrorNote>;
  const p = pay.data;
  const refresh = () => Promise.all(['admin-transaction', 'admin-payment', 'admin-payment-after', 'admin-payment-emails', 'admin-ledger'].map(k => qc.invalidateQueries({ queryKey: [k] })));
  const action = { payment_id: p.id, method: p.provider, provider_ref: p.provider_payment_id, amount_pence: p.amount_pence, outstanding_pence: line.outstanding_pence, description: p.description, status: p.status };
  const balances = (after.data ?? []).filter(r => r.kind === 'payment');
  const refunds = (after.data ?? []).filter(r => r.kind !== 'payment');
  const recorder = p.recorded_by ? names.data?.get(p.recorded_by) : undefined;
  const byHand = ['cash', 'bank_transfer', 'instructor'].includes(p.provider);
  const items = p.memberships.length + p.bookings.length + p.fees.length;

  return (
    <div className="space-y-6">
      <p className="text-sm text-ink-500"><Link to="/admin/payments" className="hover:underline">Payments</Link> / {p.household ? <Link to={adminLinks.household(p.household.id)} className="hover:underline">{p.household.name}</Link> : null}</p>

      <section className="card space-y-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold">{money(p.amount_pence)} · {line.what_for === 'membership' ? 'Membership' : line.what_for_detail ?? p.description ?? 'Payment'}{p.parent_payment_id && <span className="font-normal text-ink-500"> (balance payment)</span>}</h2>
            <p className="text-ink-500">{ledgerHow(line)} · {date(line.on_date)}{line.people.length > 0 && <> · for {line.people.map((x, i) => <span key={x.id}>{i > 0 && ', '}<Link to={adminLinks.person(x.id)} className="link">{x.name}</Link></span>)}</>}</p>
          </div>
          <Pill s={p.status} />
        </div>
        {line.outstanding_pence > 0 && <p className="font-semibold text-brand-700">{money(line.outstanding_pence)} outstanding</p>}
        {canEdit && !acting && (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {byHand && ['confirmed', 'paid_out'].includes(p.status) && (receipt ? <span className="text-xs text-ink-500">{receipt}</span> : <button className={ROW_ACTION} onClick={async () => {
              setReceipt('Sending…');
              try { await api('/admin/payments/receipt', { method: 'POST', body: { payment_id: p.id } }); setReceipt('Receipt sent'); await refresh(); } catch { setReceipt('Could not send the receipt'); }
            }}><Mail size={12} /> Email receipt</button>)}
            <PaymentActions p={action} onOpen={setActing} className="" />
          </div>
        )}
        {acting && <PaymentActionForm p={action} action={acting} onClose={() => setActing(null)} onDone={refresh} />}
      </section>

      <section className="card">
        <h3 className="mb-2 font-semibold">Details</h3>
        <dl className="text-[15px]">
          <Row label="Amount received">{money(p.amount_pence)}</Row>
          <Row label="Discount">{p.discount_pence > 0 && <>{money(p.discount_pence)}{p.discount_reason && <span className="text-ink-500"> · {p.discount_reason}</span>}</>}</Row>
          <Row label="Gross amount">{p.discount_pence > 0 && money(p.amount_pence + p.discount_pence)}</Row>
          <Row label="Payment method">{p.provider === 'instructor' ? 'Collected by instructor' : ledgerHow(line)}</Row>
          <Row label="Collected by">{personLink(p.collected_by)}</Row>
          <Row label="Date received">{date(p.charge_date)}</Row>
          <Row label="Recorded">{dateTime(p.created_at)}{recorder ? <> by {personLink(recorder)}</> : p.recorded_by ? ' by an admin' : ''}</Row>
          <Row label="Reference">{p.reference}</Row>
          <Row label={p.provider === 'paypal' ? 'PayPal' : 'GoCardless'}>{p.provider_payment_id && (p.provider === 'gocardless' || p.provider === 'paypal') && (
            <a href={p.provider === 'paypal' ? paypal.transaction(p.provider_payment_id) : gocardless.payment(p.provider_payment_id)} target="_blank" rel="noreferrer" className="link inline-flex items-center gap-1">{p.provider_payment_id} <ExternalLink size={12} /></a>)}</Row>
          <Row label="Balance payment of">{p.parent_payment_id && <Link to={adminLinks.payment(p.parent_payment_id)} className="link">the original payment</Link>}</Row>
          <Row label="Note">{p.note}</Row>
          <Row label="Description">{p.description}</Row>
        </dl>
      </section>

      {items > 0 && (
        <section className="card space-y-2">
          <h3 className="font-semibold">Allocation <span className="font-normal text-ink-500">({items} {items === 1 ? 'item' : 'items'})</span></h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-ink-500"><tr><th className="py-2 pr-3 font-medium">Member</th><th className="pr-3 font-medium">Item</th><th className="pr-3 font-medium">Status</th><th className="text-right font-medium">Price</th></tr></thead>
              <tbody className="divide-y divide-stone-100">
                {p.memberships.map(m => <tr key={m.id}><td className="py-2 pr-3">{personLink(m.person)}</td><td className="pr-3">{m.plan?.name ?? 'Membership'} · {date(m.starts_on)} to {date(m.ends_on)}</td><td className="pr-3 capitalize">{m.status}</td><td className="text-right">{money(m.price_pence)}</td></tr>)}
                {p.bookings.map(b => <tr key={b.id}><td className="py-2 pr-3">{personLink(b.person)}</td><td className="pr-3">{b.event && <Link to={adminLinks.event(b.event.id)} className="link">{b.event.title}</Link>}{b.option && <> · {b.option.name}</>}</td><td className="pr-3 capitalize">{b.status}</td><td className="text-right">{money(b.price_pence)}</td></tr>)}
                {p.fees.map(g => <tr key={g.id}><td className="py-2 pr-3">{personLink(g.person)}</td><td className="pr-3">Grading fee, {gradeLabel(g.for_rank)}{g.event && <> · <Link to={adminLinks.event(g.event.id)} className="link">{g.event.title}</Link></>}</td><td className="pr-3">Paid</td><td className="text-right">{g.fee_pence != null ? money(g.fee_pence) : ''}</td></tr>)}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {(balances.length > 0 || refunds.length > 0) && (
        <section className="card space-y-2">
          <h3 className="font-semibold">Related transactions</h3>
          <ul className="divide-y divide-stone-100 text-sm">
            {[...balances, ...refunds].sort((a, b) => a.created_at.localeCompare(b.created_at)).map(r => (
              <li key={r.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2">
                <span><Link to={adminLinks.payment(r.id)} className="link">{r.kind === 'payment' ? 'Balance payment' : r.kind === 'refund' ? 'Refund' : 'Chargeback'}</Link>
                  {r.kind !== 'payment' && r.description && <span className="text-ink-500"> · {r.description}</span>}</span>
                <span className="flex items-center gap-3 text-ink-500">{date(r.on_date)} · {ledgerHow(r)} · <span className={r.amount_pence < 0 ? 'text-brand-700' : 'text-ink-900'}>{money(r.amount_pence)}</span> <Pill s={r.status} /></span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="card space-y-2">
        <h3 className="font-semibold">Emails</h3>
        {emails.isLoading ? <Spinner /> : <EmailsSent rows={emails.data ?? []} empty="No receipt has been emailed for this payment." />}
      </section>

      <History records={[id, ...(after.data ?? []).map(r => r.id)]} title="History" />
    </div>
  );
}

interface RefundRecord {
  id: string; payment_id: string; household_id: string; kind: string; method: string; provider_refund_id: string | null; amount_pence: number;
  status: string; reason: string; reference: string | null; note: string | null; refunded_on: string | null; recorded_by: string | null; created_at: string;
  household: { id: string; name: string } | null;
}

function RefundView({ line }: { line: LedgerRow }) {
  const id = line.id;
  const refund = useQuery({
    queryKey: ['admin-refund', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('refund')
        .select('id,payment_id,household_id,kind,method,provider_refund_id,amount_pence,status,reason,reference,note,refunded_on,recorded_by,created_at,household:household_id(id,name)')
        .eq('id', id).single();
      if (error) throw error;
      return data as unknown as RefundRecord;
    },
  });
  const paid = useQuery({
    queryKey: ['admin-transaction', line.payment_id],
    queryFn: async () => {
      const { data, error } = await supabase.from('ledger').select('*').eq('id', line.payment_id).maybeSingle();
      if (error) throw error;
      return data as LedgerRow | null;
    },
  });
  const emails = useQuery({
    queryKey: ['admin-refund-emails', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('email_log').select(EMAIL_COLUMNS).eq('dedupe_key', `refund:${id}`).order('created_at', { ascending: false });
      if (error) throw error;
      return data as EmailRow[];
    },
  });
  const names = useNamesByLogin([refund.data?.recorded_by]);
  if (refund.isLoading) return <Spinner />;
  if (refund.error || !refund.data) return <ErrorNote>Could not load this refund.</ErrorNote>;
  const r = refund.data;
  const label = r.kind === 'chargeback' ? 'Chargeback' : 'Refund';
  const recorder = r.recorded_by ? names.data?.get(r.recorded_by) : undefined;

  return (
    <div className="space-y-6">
      <p className="text-sm text-ink-500"><Link to="/admin/payments" className="hover:underline">Payments</Link> / {r.household ? <Link to={adminLinks.household(r.household.id)} className="hover:underline">{r.household.name}</Link> : null}</p>

      <section className="card space-y-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold">{label} {money(r.amount_pence)} · {line.what_for === 'membership' ? 'Membership' : line.what_for_detail ?? 'Payment'}</h2>
            <p className="text-ink-500">{ledgerHow(line)} · {date(line.on_date)}{line.people.length > 0 && <> · for {line.people.map((x, i) => <span key={x.id}>{i > 0 && ', '}<Link to={adminLinks.person(x.id)} className="link">{x.name}</Link></span>)}</>}</p>
          </div>
          <Pill s={r.status} />
        </div>
        {r.status === 'failed' && r.note && <p className="font-semibold text-brand-700">Refused: {r.note}</p>}
      </section>

      <section className="card">
        <h3 className="mb-2 font-semibold">Details</h3>
        <dl className="text-[15px]">
          <Row label="Amount">{money(r.amount_pence)}</Row>
          <Row label="Refund method">{ledgerHow(line)}</Row>
          <Row label="Reason (the household sees this)">{r.reason}</Row>
          <Row label="Refunded on">{date(r.refunded_on)}</Row>
          <Row label="Recorded">{dateTime(r.created_at)}{recorder ? <> by {personLink(recorder)}</> : r.recorded_by ? ' by an admin' : ''}</Row>
          <Row label="Reference">{r.reference}</Row>
          <Row label={r.method === 'paypal' ? 'PayPal refund' : 'GoCardless refund'}>{r.provider_refund_id && (r.method === 'gocardless'
            ? <a href={gocardless.refund(r.provider_refund_id)} target="_blank" rel="noreferrer" className="link inline-flex items-center gap-1">{r.provider_refund_id} <ExternalLink size={12} /></a>
            : r.provider_refund_id)}</Row>
          <Row label="Provider's note">{r.status !== 'failed' && r.note}</Row>
        </dl>
      </section>

      <section className="card space-y-2">
        <h3 className="font-semibold">Original payment</h3>
        {paid.data ? (
          <p className="text-[15px]"><Link to={adminLinks.payment(paid.data.id)} className="link">{money(paid.data.amount_pence)} · {paid.data.what_for === 'membership' ? 'Membership' : paid.data.what_for_detail ?? paid.data.description ?? 'Payment'}</Link>
            <span className="text-ink-500"> · {ledgerHow(paid.data)} · {date(paid.data.on_date)}</span> <Pill s={paid.data.status} /></p>
        ) : <p className="text-sm text-ink-500">The payment could not be found.</p>}
      </section>

      <section className="card space-y-2">
        <h3 className="font-semibold">Emails</h3>
        {emails.isLoading ? <Spinner /> : <EmailsSent rows={emails.data ?? []} empty={r.status === 'paid' ? 'No email was sent for this refund.' : 'The household is emailed when the refund is paid.'} />}
      </section>

      <History records={[id]} title="History" />
    </div>
  );
}
